import {
  AdminSendBroadcastDTOType,
  AdminSendBulkNotificationDTOType,
  AdminSendNotificationDTOType,
  components,
  PathsAdminNotificationsGetParametersQuerySource,
  PathsAdminNotificationsGetParametersQueryType,
} from '@coffice/shared/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/api/client';
import { useAuth } from '@/lib/auth';
import { queryKeys } from '@/lib/queryKeys';

export type NotificationType = PathsAdminNotificationsGetParametersQueryType;
export type NotificationSourceType =
  PathsAdminNotificationsGetParametersQuerySource;
export { PathsAdminNotificationsGetParametersQuerySource as NotificationSource };

// Extended notification type that includes user relation and grouped stats from admin endpoint
export type Notification = components['schemas']['Notification'] & {
  user?: {
    id: number;
    firstName: string;
    lastName: string;
  };
  // Grouped broadcast stats (only present when grouped=true)
  recipientCount?: number;
  pushSentCount?: number;
  readCount?: number;
};

export interface NotificationFilters {
  type: string;
}

interface UseNotificationsParams {
  page?: number;
  limit?: number;
  type?: NotificationType;
  source?: NotificationSourceType;
  userId?: number;
  grouped?: boolean;
}

export const useNotifications = (params: UseNotificationsParams = {}) => {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const { page = 1, limit = 20, type, source, userId, grouped } = params;

  return useQuery({
    queryKey: [
      queryKeys.ADMIN_NOTIFICATIONS,
      { page, limit, type, source, userId, grouped },
    ],
    queryFn: async () => {
      const response = await apiClient.GET('/admin/notifications', {
        params: {
          query: {
            page,
            limit,
            type: type || undefined,
            source: source || undefined,
            userId: userId || undefined,
            grouped: grouped || undefined,
          },
        },
      });

      if (response.error) {
        throw new Error('Failed to fetch notifications');
      }

      const headers = response.response.headers;
      return {
        notifications: (response.data || []) as Notification[],
        total: parseInt(headers.get('X-Total-Count') || '0'),
        totalPages: parseInt(headers.get('X-Total-Pages') || '1'),
        currentPage: parseInt(headers.get('X-Current-Page') || '1'),
      };
    },
    enabled: isAuthenticated && !isAuthLoading,
  });
};

export const useNotificationMutations = () => {
  const queryClient = useQueryClient();

  const sendNotificationMutation = useMutation({
    mutationFn: async (data: {
      userId: number;
      title: string;
      body: string;
      type: AdminSendNotificationDTOType;
      data?: Record<string, unknown>;
    }) => {
      const response = await apiClient.POST('/admin/notifications/send', {
        body: data,
      });

      if (response.error) {
        throw new Error('Failed to send notification');
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_NOTIFICATIONS],
      });
    },
  });

  const sendBulkNotificationMutation = useMutation({
    mutationFn: async (data: {
      userIds: number[];
      title: string;
      body: string;
      type: AdminSendBulkNotificationDTOType;
      data?: Record<string, unknown>;
    }) => {
      const response = await apiClient.POST('/admin/notifications/send-bulk', {
        body: data,
      });

      if (response.error) {
        throw new Error('Failed to send bulk notification');
      }

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_NOTIFICATIONS],
      });
    },
  });

  const broadcastNotificationMutation = useMutation({
    mutationFn: async (data: {
      title: string;
      body: string;
      type: AdminSendBroadcastDTOType;
      data?: Record<string, unknown>;
    }) => {
      const response = await apiClient.POST('/admin/notifications/broadcast', {
        body: data,
      });

      if (response.error) {
        throw new Error('Failed to broadcast notification');
      }

      return response.data as { sent: number };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.ADMIN_NOTIFICATIONS],
      });
    },
  });

  return {
    sendNotification: sendNotificationMutation.mutateAsync,
    sendBulkNotification: sendBulkNotificationMutation.mutateAsync,
    broadcastNotification: broadcastNotificationMutation.mutateAsync,
    isSending:
      sendNotificationMutation.isPending ||
      sendBulkNotificationMutation.isPending ||
      broadcastNotificationMutation.isPending,
  };
};
